import { getKey } from '../utils/getKey';

/**
 * Manages the state of the burger menu and the body element.
 * Toggles ARIA attributes, CSS properties, and data attributes to
 * visually open or close the menu. Uses a setTimeout for animations.
 *
 * @param {boolean} burgerIsPressed The current 'aria-pressed' state of the burger button.
 * @param {HTMLElement} bodyEl The document body element.
 * @param {HTMLElement} burgerTargetEl The navigation menu element controlled by the burger button.
 * @returns {void}
 */
function setBurgerState(burgerIsPressed, bodyEl, burgerTargetEl) {
  if (!burgerIsPressed) {
    bodyEl.setAttribute('data-menuopen', '');
    window.setTimeout(() => {
      burgerTargetEl.setAttribute('data-is-open', '');
    }, 50);
  } else {
    bodyEl.removeAttribute('data-menuopen');
    burgerTargetEl.removeAttribute('data-is-open');
  }
}

/**
 * Initializes the main navigation burger menu functionality.
 * Sets up event listeners, manage ARIA attributes and CSS classes
 *
 * @returns {void}
 */
export function initMainNavBurger() {
  const burgerSelector = '.burger';
  const burgerEl = document.querySelector(burgerSelector);
  if (!burgerEl) return;

  const bodyEl = document.querySelector('body');

  const burgerTargetEl = document.getElementById(burgerEl.getAttribute('aria-controls'));
  if (!burgerTargetEl) return;

  /**
   * Handles the click event on the burger button.
   * Toggles the menu's state and updates ARIA attributes.
   *
   * @param {MouseEvent} event The click event object.
   * @returns {void}
   */
  burgerEl.addEventListener('click', (event) => {
    const burgerIsPressed = event.currentTarget.getAttribute('aria-pressed') === 'true';

    event.currentTarget.setAttribute('aria-pressed', !burgerIsPressed);
    event.currentTarget.setAttribute('aria-expanded', !burgerIsPressed);

    setBurgerState(burgerIsPressed, bodyEl, burgerTargetEl);

    event.currentTarget.focus({ focusVisible: true });
  });

  /**
   * Handles the keyup event on the burger button.
   * Specifically listens for the Escape key to close the menu.
   *
   * @param {KeyboardEvent} event The keyup event object.
   * @returns {void}
   */
  burgerEl.addEventListener('keyup', (event) => {
    const { isEscape } = getKey(event);
    if (isEscape) {
      event.currentTarget.setAttribute('aria-pressed', false);
      event.currentTarget.setAttribute('aria-expanded', false);

      setBurgerState(true, bodyEl, burgerTargetEl);

      event.currentTarget.focus({ focusVisible: true });
    }
  });
}
